/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * Audit usage counter widget for the dashboard.
 * Format: "Fresh audits: X of Y used this month. Resets in Z days."
 * Color bands: green | orange (X==Y-1) | red (X==Y).
 * When limit is hit, the parent component shows the upgrade CTA instead of
 * the normal Run-Audit button (caller controls that via `onLimitReached`).
 */
export default function AuditUsageCounter({ onUsageChange }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/audit/me/usage").then(({ data }) => {
      setUsage(data);
      if (onUsageChange) onUsageChange(data);
    }).catch(() => {});
  }, []);

  if (!usage) return null;
  const { used, limit, days_to_reset } = usage;
  const remaining = Math.max(0, limit - used);
  const atLimit = used >= limit;
  const oneLeft = !atLimit && remaining === 1;

  const color = atLimit ? "var(--rg-red)" : oneLeft ? "var(--rg-amber)" : "var(--rg-sage)";
  const bg = atLimit ? "var(--rg-red-soft)" : oneLeft ? "var(--rg-amber-soft)" : "var(--rg-sage-soft)";

  return (
    <div
      className="rounded-md border px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
      style={{ borderColor: color, background: bg }}
      data-testid="audit-usage-counter"
    >
      <div className="text-sm">
        <span className="font-semibold" style={{ color }}>
          Fresh audits: {used} of {limit} used this month.
        </span>{" "}
        <span className="text-[var(--rg-text)]">Resets in {days_to_reset} day{days_to_reset === 1 ? "" : "s"}.</span>
      </div>
      {atLimit ? (
        <div className="flex items-center gap-2" data-testid="audit-limit-reached">
          <span className="text-xs text-[var(--rg-red)] font-semibold inline-flex items-center gap-1">
            <Lock className="w-3.5 h-3.5" /> Monthly audit limit reached
          </span>
          <Link to="/billing">
            <Button size="sm" className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="audit-upgrade-cta">
              Upgrade for more audits <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      ) : (
        <Link to="/onboarding" data-testid="audit-run-link">
          <Button size="sm" className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Run a fresh audit
          </Button>
        </Link>
      )}
    </div>
  );
}
